import type { ColoredMaterial } from "@color-finder/color-finder-api/models";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import { ColoredMaterialDetails } from "components/coloredMaterialDetails/ColoredMaterialDetails";
import ColorStatusDisplay from "components/dataDisplay/ColorStatusDisplay";
import { OpacityDisplay } from "components/dataDisplay/OpacityDisplay";
import { useIsBelowDesktop } from "hooks/use-breakpoint";
import {
  gradeValue,
  IndustryInternal,
  InternalApplication,
  RestrictedApplication,
  TrialApplication,
} from "hooks/utils/material-data";
import {
  SettingsContext,
  type SettingsContextValue,
} from "providers/SettingsProvider";
import { type Dispatch, type FC, useContext, useMemo } from "react";

export interface ColoredMaterialDetailsDialogProps {
  material: ColoredMaterial;
  open: boolean;
  isInternal: boolean;
  onClose: Dispatch<any>;
}

export const ColoredMaterialDetailsDialog: FC<
  ColoredMaterialDetailsDialogProps
> = ({ material, isInternal, open, onClose }) => {
  const fullScreen = useIsBelowDesktop();
  const { hierachyData } = useContext<SettingsContextValue>(SettingsContext);
  const grade = gradeValue({ hierachyData, material });

  const isInternalOnly = useMemo(
    () => InternalApplication.some((i) => grade?.class.includes(i)),
    [grade],
  );
  const isRestrcited = useMemo(
    () => RestrictedApplication.some((i) => grade?.class.includes(i)),
    [grade],
  );
  const isTrial = useMemo(
    () => TrialApplication.some((i) => grade?.class.includes(i)),
    [grade],
  );
  const isInternalIndustry = useMemo(
    () => IndustryInternal.some((i) => grade?.industry.includes(i)),
    [grade],
  );

  return (
    <Dialog
      maxWidth={"lg"}
      fullScreen={fullScreen}
      open={open}
      onClose={onClose}
      scroll={fullScreen ? "body" : "paper"}
    >
      <DialogTitle
        borderBottom={(theme) => `1px solid ${theme.palette.divider}`}
      >
        <Stack direction="row" alignItems="center" gap={4}>
          <span>{material.material.publicGradeName}</span>
          <span>
            {material.color.finalColorCode ??
              material.color.preliminaryColorCode}
          </span>
          <OpacityDisplay label value={material.color.opacity} />
          <Box ml="auto">
            <ColorStatusDisplay
              variant="filled"
              size="medium"
              value={material.availability.colorStatus}
            />
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={{
          backgroundColor: (theme) => theme.palette.background.default,
          fontSize: "var(--body-b2-14-regular-size, 14px)",
          padding: 0,
        }}
      >
        <ColoredMaterialDetails
          material={material}
          isInternal={isInternal}
          grade={grade}
          isInternalOnly={isInternalOnly}
          isRestrcited={isRestrcited}
          isTrial={isTrial}
          isInternalIndustry={isInternalIndustry}
        />
      </DialogContent>
      <DialogActions
        sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}` }}
      >
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
